import { CancelToken } from 'axios'
import { APIResponseWrapper, CB_BASE_PREFIX } from '@/api/types'
import { callBackend } from '@/api/callBackend'
import { ApiMethod, EmptyResponse } from 'common/src/api/types'
import {
    CancelAgentContentJobRequest,
    CancelAgentContentJobResponse,
    CreateSectionCheckpointRequest,
    CreateSectionCheckpointResponse,
    GetAgentActivityResponse,
    GetAgentContentJobRequest,
    GetAgentContentJobResponse,
    GetAgentJobDiffRequest,
    GetNotecardAgentJobsRequest,
    GetNotecardAgentJobsResponse,
    GetAgentJobDiffResponse,
    GetAgentInstructorContextResponse,
    ListAgentContentJobsForCourseRequest,
    ListAgentContentJobsForCourseResponse,
    ListAgentContentJobsForSectionRequest,
    ListAgentContentJobsForSectionResponse,
    ListAgentJobHistoryRequest,
    ListAgentJobHistoryResponse,
    ListSectionCheckpointsRequest,
    ListSectionCheckpointsResponse,
    RestoreSectionCheckpointRequest,
    RevertAgentJobRunRequest,
    RevertAgentJobRunResponse,
    SetAgentInstructorContextRequest,
    StartAgentContentJobRequest,
    StartAgentContentJobResponse,
    StageSourceMaterialFileResponse,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import {
    GetPreviousExamFileSetRequest,
    GetPreviousExamFileSetResponse,
    ListPreviousExamFileSetsRequest,
    ListPreviousExamFileSetsResponse,
    ListPreviousExamMappingDecisionsRequest,
    ListPreviousExamProblemsRequest,
    ListPreviousExamProblemsResponse,
    ListPreviousExamMappingDecisionsResponse,
    PreviousExamFileSlot,
    PreviousExamIdentifier,
    ResolvePreviousExamMappingDecisionRequest,
    ResolvePreviousExamMappingDecisionResponse,
    UploadPreviousExamFileResponse,
} from 'common/src/api/contentFrontendVsContentBackend/previousExamAuthoring'

const PREFIX = CB_BASE_PREFIX + '/agentContent'

// No retries for mutating calls — a retried timeout would start a duplicate job / double-restore
const MUTATING_NUM_RETRIES = 1

export const getAgentActivity = async (
    cancelToken: CancelToken | null = null
): APIResponseWrapper<GetAgentActivityResponse> => {
    return await callBackend<null, GetAgentActivityResponse>(
        PREFIX + '/getAgentActivity',
        ApiMethod.GET,
        null,
        cancelToken
    )
}

export const cancelAgentContentJob = async (
    payload: CancelAgentContentJobRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<CancelAgentContentJobResponse> => {
    return await callBackend<
        CancelAgentContentJobRequest,
        CancelAgentContentJobResponse
    >(
        PREFIX + '/cancelJob',
        ApiMethod.POST,
        payload,
        cancelToken,
        MUTATING_NUM_RETRIES
    )
}

export const startAgentContentJob = async (
    payload: StartAgentContentJobRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<StartAgentContentJobResponse> => {
    return await callBackend<
        StartAgentContentJobRequest,
        StartAgentContentJobResponse
    >(
        PREFIX + '/startJob',
        ApiMethod.POST,
        payload,
        cancelToken,
        MUTATING_NUM_RETRIES
    )
}

export const stageSourceMaterialFile = async (
    courseName: string,
    file: File,
    stagedImportId?: string,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<StageSourceMaterialFileResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('courseName', courseName)
    if (stagedImportId) formData.append('stagedImportId', stagedImportId)
    return await callBackend<FormData, StageSourceMaterialFileResponse>(
        PREFIX + '/stageSourceMaterialFile',
        ApiMethod.POST,
        formData,
        cancelToken,
        MUTATING_NUM_RETRIES
    )
}

export const uploadPreviousExamFile = async (
    courseName: string,
    examIdentifier: PreviousExamIdentifier,
    slot: PreviousExamFileSlot,
    file: File,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<UploadPreviousExamFileResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('courseName', courseName)
    formData.append('year', String(examIdentifier.year))
    formData.append('sittingSeason', examIdentifier.sittingSeason)
    formData.append(
        'originalExamAbbreviation',
        examIdentifier.originalExamAbbreviation
    )
    formData.append('slot', slot)
    return await callBackend<FormData, UploadPreviousExamFileResponse>(
        PREFIX + '/uploadPreviousExamFile',
        ApiMethod.POST,
        formData,
        cancelToken,
        MUTATING_NUM_RETRIES
    )
}

export const getPreviousExamFileSet = async (
    params: GetPreviousExamFileSetRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<GetPreviousExamFileSetResponse> =>
    await callBackend<
        GetPreviousExamFileSetRequest,
        GetPreviousExamFileSetResponse
    >(PREFIX + '/getPreviousExamFileSet', ApiMethod.GET, params, cancelToken)

export const listPreviousExamFileSets = async (
    params: ListPreviousExamFileSetsRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ListPreviousExamFileSetsResponse> =>
    await callBackend<
        ListPreviousExamFileSetsRequest,
        ListPreviousExamFileSetsResponse
    >(PREFIX + '/listPreviousExamFileSets', ApiMethod.GET, params, cancelToken)

export const listPreviousExamMappingDecisions = async (
    params: ListPreviousExamMappingDecisionsRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ListPreviousExamMappingDecisionsResponse> =>
    await callBackend<
        ListPreviousExamMappingDecisionsRequest,
        ListPreviousExamMappingDecisionsResponse
    >(
        PREFIX + '/listPreviousExamMappingDecisions',
        ApiMethod.GET,
        params,
        cancelToken
    )

export const listPreviousExamProblems = async (
    params: ListPreviousExamProblemsRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ListPreviousExamProblemsResponse> =>
    await callBackend<
        ListPreviousExamProblemsRequest,
        ListPreviousExamProblemsResponse
    >(PREFIX + '/listPreviousExamProblems', ApiMethod.GET, params, cancelToken)

export const resolvePreviousExamMappingDecision = async (
    payload: ResolvePreviousExamMappingDecisionRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ResolvePreviousExamMappingDecisionResponse> =>
    await callBackend<
        ResolvePreviousExamMappingDecisionRequest,
        ResolvePreviousExamMappingDecisionResponse
    >(
        PREFIX + '/resolvePreviousExamMappingDecision',
        ApiMethod.POST,
        payload,
        cancelToken,
        MUTATING_NUM_RETRIES
    )

export const getAgentContentJob = async (
    params: GetAgentContentJobRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<GetAgentContentJobResponse> => {
    return await callBackend<
        GetAgentContentJobRequest,
        GetAgentContentJobResponse
    >(PREFIX + '/getJob', ApiMethod.GET, params, cancelToken)
}

export const getNotecardAgentJobs = async (
    params: GetNotecardAgentJobsRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<GetNotecardAgentJobsResponse> => {
    return await callBackend<
        GetNotecardAgentJobsRequest,
        GetNotecardAgentJobsResponse
    >(PREFIX + '/getNotecardAgentJobs', ApiMethod.GET, params, cancelToken)
}

export const getAgentJobDiff = async (
    params: GetAgentJobDiffRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<GetAgentJobDiffResponse> => {
    return await callBackend<GetAgentJobDiffRequest, GetAgentJobDiffResponse>(
        PREFIX + '/getJobDiff',
        ApiMethod.GET,
        params,
        cancelToken
    )
}

export const listAgentContentJobsForSection = async (
    payload: ListAgentContentJobsForSectionRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ListAgentContentJobsForSectionResponse> => {
    return await callBackend<
        ListAgentContentJobsForSectionRequest,
        ListAgentContentJobsForSectionResponse
    >(PREFIX + '/listJobsForSection', ApiMethod.POST, payload, cancelToken)
}

export const listAgentContentJobsForCourse = async (
    payload: ListAgentContentJobsForCourseRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ListAgentContentJobsForCourseResponse> => {
    return await callBackend<
        ListAgentContentJobsForCourseRequest,
        ListAgentContentJobsForCourseResponse
    >(PREFIX + '/listJobsForCourse', ApiMethod.POST, payload, cancelToken)
}

export const listAgentJobHistory = async (
    payload: ListAgentJobHistoryRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ListAgentJobHistoryResponse> => {
    return await callBackend<
        ListAgentJobHistoryRequest,
        ListAgentJobHistoryResponse
    >(PREFIX + '/listJobHistory', ApiMethod.POST, payload, cancelToken)
}

export const getAgentInstructorContext = async (
    cancelToken: CancelToken | null = null
): APIResponseWrapper<GetAgentInstructorContextResponse> => {
    return await callBackend<null, GetAgentInstructorContextResponse>(
        PREFIX + '/getInstructorContext',
        ApiMethod.GET,
        null,
        cancelToken
    )
}

export const setAgentInstructorContext = async (
    payload: SetAgentInstructorContextRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<EmptyResponse> => {
    return await callBackend<SetAgentInstructorContextRequest, EmptyResponse>(
        PREFIX + '/setInstructorContext',
        ApiMethod.POST,
        payload,
        cancelToken
    )
}

export const createSectionCheckpoint = async (
    payload: CreateSectionCheckpointRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<CreateSectionCheckpointResponse> => {
    return await callBackend<
        CreateSectionCheckpointRequest,
        CreateSectionCheckpointResponse
    >(
        PREFIX + '/createCheckpoint',
        ApiMethod.POST,
        payload,
        cancelToken,
        MUTATING_NUM_RETRIES
    )
}

export const listSectionCheckpoints = async (
    payload: ListSectionCheckpointsRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<ListSectionCheckpointsResponse> => {
    return await callBackend<
        ListSectionCheckpointsRequest,
        ListSectionCheckpointsResponse
    >(PREFIX + '/listCheckpoints', ApiMethod.POST, payload, cancelToken)
}

export const restoreSectionCheckpoint = async (
    payload: RestoreSectionCheckpointRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<EmptyResponse> => {
    return await callBackend<RestoreSectionCheckpointRequest, EmptyResponse>(
        PREFIX + '/restoreCheckpoint',
        ApiMethod.POST,
        payload,
        cancelToken,
        MUTATING_NUM_RETRIES
    )
}

// One-click full undo of a courseWideEdit run: every applied section's
// pre-run checkpoint restored as a unit
export const revertAgentJobRun = async (
    payload: RevertAgentJobRunRequest,
    cancelToken: CancelToken | null = null
): APIResponseWrapper<RevertAgentJobRunResponse> => {
    return await callBackend<
        RevertAgentJobRunRequest,
        RevertAgentJobRunResponse
    >(
        PREFIX + '/revertRun',
        ApiMethod.POST,
        payload,
        cancelToken,
        MUTATING_NUM_RETRIES
    )
}
